// App.js

import React, { useState, useEffect } from "react";

import { BottomNavigation, BottomNavigationAction } from "@mui/material";

import LockIcon from "@mui/icons-material/Lock";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ScienceIcon from "@mui/icons-material/Science";
import PersonIcon from "@mui/icons-material/Person";

import { Typography, Button } from "@mui/material";
import { Box, Avatar } from "@mui/material";
import UserAuth from "./components/userAuth";
import AuthCodeManager from "./components/AuthCodeManager";
// import TradeLog from "./components/TradeLog";
// import StrategyBuilder from "./components/StrategyBuilder";
// import StrategyView from "./components/StrategyView";
import api from "./api";
import Loader from "./components/common/loader";
import EliteStrategiesTradeLogs from "./components/EliteStrategiesTradeLogs";

// import LiveTrade from "./components/LiveTrade";
import LiveTrade from "./components/LiveTrade_v2";

import Backtesting from "./components/Backtesting";

const ProfileView = ({ token, onLogout }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.post(
          "/user-profile",
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data?.success) setProfile(res.data.user);
        else setError(res.data?.message || "Failed to fetch profile");
      } catch (err) {
        console.error("❌ Error fetching profile:", err);
        setError(err.response?.data?.message || "Server error");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [token]);

  if (loading) return <Loader loading={loading} />;

  if (error)
    return (
      <p style={{ color: "red", textAlign: "center", marginTop: "20px" }}>
        {error}
      </p>
    );

  if (!profile) return null;

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        maxWidth: 420,
        width: "100%",

        background: "rgba(255,255,255,0.6)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",

        border: "1px solid rgba(255,255,255,0.4)",

        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",

        transition: "all 0.2s ease",

        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
        },
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Profile
      </Typography>{" "}
      <p>
        <strong>Name:</strong> {profile.firstName} {profile.lastName}
      </p>
      <p>
        <strong>Email:</strong> {profile.email}
      </p>
      <p>
        <strong>Phone:</strong> {profile.phone_number}
      </p>
      <p>
        <strong>Username:</strong> {profile.username}
      </p>
      <p>
        <strong>Status:</strong>{" "}
        <span
          style={{
            color: profile.status === 1 ? "green" : "red",
            fontWeight: "bold",
          }}
        >
          {profile.status === 1 ? "Active" : "Inactive"}
        </span>
      </p>
      {/* Logout Button */}
      <Button
        onClick={onLogout}
        sx={{
          mt: 2,
          px: 2,
          py: 1.2,
          borderRadius: 999,
          fontWeight: 700,

          background: "rgba(198,40,40,0.15)",
          border: "1px solid rgba(198,40,40,0.4)",
          color: "#b71c1c",

          "&:hover": {
            background: "rgba(198,40,40,0.25)",
            transform: "scale(1.05)",
          },
        }}
      >
        Logout
      </Button>
    </Box>
  );
};

function App() {
  const [activeTab, setActiveTab] = useState("auth");
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 1500); // show for 1.5s
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleSwitchTab = (e) => setActiveTab(e.detail);
    window.addEventListener("switchTab", handleSwitchTab);
    return () => window.removeEventListener("switchTab", handleSwitchTab);
  }, []);

  useEffect(() => {
    const handleForceLogout = () => handleLogout();
    window.addEventListener("forceLogout", handleForceLogout);
    return () => window.removeEventListener("forceLogout", handleForceLogout);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const username = localStorage.getItem("username");
    const user_id = localStorage.getItem("user_id");
    const email = localStorage.getItem("email");
    const firstName = localStorage.getItem("firstName");
    const lastName = localStorage.getItem("lastName");

    if (token && username && user_id) {
      setUser({ username, user_id, token, email, firstName, lastName });

      // setActiveTab("strategyBuilder");

      setActiveTab("eliteStrategies");
    }
    setAuthChecked(true);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    setUser(null);
    setActiveTab("auth");
  };

  const renderContent = () => {
    if (!authChecked) return <Loader loading={true} />;

    if (!user) return <UserAuth setUser={setUser} />;

    switch (activeTab) {
      case "auth":
        return <AuthCodeManager />;

      // case "strategyBuilder":
      //   return <StrategyBuilder />;
      // case "strategyView":
      //   return <StrategyView />;
      // case "trades":
      //   return <TradeLog />;

      case "eliteStrategies":
        return <EliteStrategiesTradeLogs />;

      case "liveTrade":
        return <LiveTrade />;

      case "backtesting":
        return <Backtesting />;

      case "profile":
        // return <ProfileView token={user.token} />;
        return <ProfileView token={user.token} onLogout={handleLogout} />;
      default:
        return null;
    }
  };

  const tabs = [
    { key: "auth", label: "Fyers Authentication" },
    // { key: "strategyBuilder", label: "Strategy Builder" },
    // { key: "strategyView", label: "View Strategy" },
    // { key: "trades", label: "Trade Log" },
    { key: "eliteStrategies", label: "Elite Strategies" },
    { key: "liveTrade", label: "Live Trade" },
    { key: "backtesting", label: "Backtesting" },
  ];

  const buttonStyle = (isActive) => ({
    padding: "8px 16px",
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 600,
    border: "1px solid rgba(255,255,255,0.4)",
    cursor: "pointer",

    backdropFilter: "blur(10px)",

    background: isActive
      ? "linear-gradient(135deg, rgba(25,118,210,0.25), rgba(25,118,210,0.08))"
      : "rgba(255,255,255,0.5)",

    color: isActive ? "#1976d2" : "#333",

    boxShadow: isActive
      ? "0 6px 20px rgba(25,118,210,0.25)"
      : "0 4px 12px rgba(0,0,0,0.08)",

    transition: "all 0.2s ease",
  });

  const buttonHoverStyle = {
    transform: "scale(1.05)",
    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
  };

  // Helper: initials from name
  const getInitials = () => {
    if (!user) return "";
    const { firstName, lastName } = user;
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  // Generate consistent color based on user_id
  const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).slice(-2);
    }
    return color;
  };

  const avatarColor = user ? stringToColor(user.user_id.toString()) : "#2563eb";

  const MobileBottomNav = () => {
    if (!user) return null;

    return (
      <Box
        sx={{
          display: { xs: "block", sm: "none" },
          position: "fixed",

          bottom: 10, // 👈 changed
          left: 10, // 👈 changed
          right: 10, // 👈 changed

          zIndex: 20,

          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",

          background: "rgba(255,255,255,0.7)",
          border: "1px solid rgba(255,255,255,0.4)",

          borderRadius: "16px", // 👈 added

          boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
        }}
      >
        <BottomNavigation
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          showLabels
          sx={{
            "& .Mui-selected": {
              color: "#1976d2",
              fontWeight: 600,
              textShadow: "0 0 10px rgba(25,118,210,0.4)",
            },
          }}
        >
          <BottomNavigationAction
            label="Auth"
            value="auth"
            icon={<LockIcon />}
            sx={{
              transition: "all 0.2s ease",
              "&:active": {
                transform: "scale(0.92)",
              },
            }}
          />

          <BottomNavigationAction
            label="Strategies"
            value="eliteStrategies"
            icon={<ShowChartIcon />}
            sx={{
              transition: "all 0.2s ease",
              "&:active": {
                transform: "scale(0.92)",
              },
            }}
          />

          <BottomNavigationAction
            label="liveTrade"
            value="liveTrade"
            icon={<ShowChartIcon />}
            sx={{
              transition: "all 0.2s ease",
              "&:active": {
                transform: "scale(0.92)",
              },
            }}
          />

          <BottomNavigationAction
            label="Backtest"
            value="backtesting"
            icon={<ScienceIcon />}
            sx={{
              transition: "all 0.2s ease",
              "&:active": {
                transform: "scale(0.92)",
              },
            }}
          />

          <BottomNavigationAction
            label="Profile"
            value="profile"
            icon={<PersonIcon />}
            sx={{
              transition: "all 0.2s ease",
              "&:active": {
                transform: "scale(0.92)",
              },
            }}
          />
        </BottomNavigation>
      </Box>
    );
  };

  if (showSplash) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "linear-gradient(to bottom right, #ffffff, #f3f4f6)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
          animation: "fadeOutScreen 6.5s ease-in-out forwards",
        }}
      >
        {/* Logo */}
        <img
          src="/4.png"
          alt="Logo"
          style={{
            width: "270px",
            height: "auto",
            opacity: 0,
            transform: "translateY(40px)",
            animation: "fadeUp 6.5s ease-in-out forwards",
          }}
        />

        {/* Text */}
        <h1
          style={{
            marginTop: "18px",
            fontSize: "34px",
            fontWeight: "700",
            letterSpacing: "1.8px",
            position: "relative",
            background:
              "linear-gradient(120deg, #8b7500, #ffd700, #fff5b1, #b8860b, #8b7500)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundSize: "400% 100%",
            opacity: 0,
            transform: "translateY(20px)",
            animation:
              "fadeText 6.5s ease-in-out forwards, shimmer 4s ease-in-out 1.8s",
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          {/* Algora */}
          ApexAlgo
        </h1>

        <style>
          {`
          @keyframes fadeUp {
            0% {
              opacity: 0;
              transform: translateY(40px);
            }
            25% {
              opacity: 1;
              transform: translateY(0);
            }
            75% {
              opacity: 1;
              transform: translateY(0);
            }
            100% {
              opacity: 0;
              transform: translateY(-10px);
            }
          }

          @keyframes fadeText {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            35% {
              opacity: 1;
              transform: translateY(0);
            }
            80% {
              opacity: 1;
              transform: translateY(0);
            }
            100% {
              opacity: 0;
              transform: translateY(-10px);
            }
          }

          /* ✨ Rich, slow metallic shimmer */
          @keyframes shimmer {
            0% {
              background-position: 250% center;
            }
            100% {
              background-position: -250% center;
            }
          }

          @keyframes fadeOutScreen {
            0% { opacity: 1; }
            95% { opacity: 1; }
            100% { opacity: 0; visibility: hidden; }
          }
        `}
        </style>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",

        background: `
      radial-gradient(circle at 20% 20%, rgba(25,118,210,0.15), transparent 40%),
      radial-gradient(circle at 80% 30%, rgba(0,200,83,0.12), transparent 40%),
      radial-gradient(circle at 50% 80%, rgba(255,82,82,0.10), transparent 40%),
      linear-gradient(135deg, #f5f7fa, #e4ecf7)
    `,
      }}
    >
      {user && (
        <Box
          component="nav"
          sx={{
            display: { xs: "none", sm: "block" }, // 👈 ADD THIS
            background: "rgba(255,255,255,0.6)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderBottom: "1px solid rgba(255,255,255,0.4)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              // maxWidth: 1200,
              mx: "auto",
              px: 2,
              height: 64,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Left side: Logo + Title + Tagline */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <img
                src="/4.png"
                alt="Logo"
                width={40}
                height={40}
                style={{
                  filter: "contrast(1.5) brightness(0.8) saturate(1.3)",
                }}
              />
              <Box
                sx={{ display: "flex", flexDirection: "column", lineHeight: 1 }}
              >
                {/* <span
                  style={{ fontSize: 22, fontWeight: 500, color: "#b3a917ff" }}
                > */}
                <span
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 22,
                    fontWeight: 800,
                    color: "#b3a917ff",
                    letterSpacing: "0.3px",
                  }}
                >
                  {/* Algora */}
                  ApexAlgo
                </span>
                <span
                  style={{ fontSize: 13, fontWeight: 400, color: "#615101ff" }}
                >
                  Algorithmic Excellence
                </span>
              </Box>
            </Box>

            {/* Desktop Tabs */}
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 1,
              }}
            >
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  style={buttonStyle(activeTab === tab.key)}
                  onMouseEnter={(e) =>
                    Object.assign(e.target.style, buttonHoverStyle)
                  }
                  onMouseLeave={(e) =>
                    Object.assign(
                      e.target.style,
                      buttonStyle(activeTab === tab.key),
                    )
                  }
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}

              {/* User email with avatar */}
              <button
                onClick={() => setActiveTab("profile")}
                style={{
                  ...buttonStyle(activeTab === "profile"),
                  backgroundColor:
                    activeTab === "profile" ? "#2563eb" : "transparent",
                  color: activeTab === "profile" ? "#fff" : "#2563eb",
                  textDecoration: "none",
                }}
              >
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: avatarColor,
                    fontSize: 14,
                  }}
                >
                  {getInitials()}
                </Avatar>
                {user.email || user.username}
              </button>
            </Box>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          p: 2,

          pb: { xs: "70px", sm: 2 },

          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",

          animation: "fadeIn 0.4s ease",

          "@keyframes fadeIn": {
            from: { opacity: 0, transform: "translateY(8px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        {renderContent()}
      </Box>
      <MobileBottomNav />
    </Box>
  );
}

export default App;
